import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiPublic } from '@/lib/api';
import { PATHS } from '@/lib/paths';
import { useAuth } from '../lib/auth';

function BugpunchLogo({ size = 56 }: { size?: number }) {
  return (
    <img
      src="/logo-512.png"
      alt="Bugpunch"
      width={size}
      height={size}
      style={{ display: 'block' }}
    />
  );
}

/** "Bug" white + "punch" orange. Echoes the orange glove in the logo. */
function BugpunchWordmark({ className = '' }: { className?: string }) {
  return (
    <span className={className}>
      <span className="text-foreground">Bug</span>
      <span style={{ color: '#f97316' }}>punch</span>
    </span>
  );
}

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [buildCommit, setBuildCommit] = useState('');

  // Subtle build marker so the signed-out surface advertises which dashboard
  // build is deployed — mirrors the authed sidebar footer (AppLayout). Health
  // is public, so this works before login.
  useEffect(() => {
    apiPublic<{ commit?: string }>(PATHS.health)
      .then((d) => {
        if (d.commit) setBuildCommit(d.commit);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.title = 'Bugpunch';
  }, []);
  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  // test_key auto-login (E2E)
  useEffect(() => {
    (async () => {
      const testKey = new URLSearchParams(window.location.search).get('test_key');
      if (!testKey) return;
      try {
        const data = await apiPublic<{ token?: string; organizations?: { Id: string }[] }>(
          PATHS.authTestLogin,
          { query: { key: testKey } },
        );
        if (!data.token) return;
        localStorage.setItem('auth_token', data.token);
        if (data.organizations?.[0])
          localStorage.setItem('active_org_id', data.organizations[0].Id);
        window.location.href = '/';
      } catch {
        /* not available */
      }
    })();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setSubmitting(true);
    try {
      if (mode === 'forgot') {
        await apiPublic(PATHS.authForgotPassword, {
          method: 'POST',
          body: { email },
        });
        setInfo('If that email is registered, a password reset link has been sent.');
        setSubmitting(false);
        return;
      }
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    }
    setSubmitting(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background"
      style={{ fontFamily: "'Geist Variable', system-ui, sans-serif" }}
    >
      {/* Suppress browser autofill yellow/green wash that reads as a "green gradient" on the form. */}
      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-text-fill-color: var(--foreground) !important;
          -webkit-box-shadow: 0 0 0 1000px var(--card) inset !important;
          caret-color: var(--foreground) !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>

      <div className="w-full max-w-[380px] mx-4">
        {/* Horizontal logo + wordmark — bigger logo, text right of it. Links
            back to the public landing page. */}
        <Link to="/" className="mb-8 flex items-center justify-center gap-3 no-underline">
          <BugpunchLogo size={56} />
          <BugpunchWordmark className="text-3xl font-semibold tracking-tight" />
        </Link>

        <div className="rounded-2xl border border-border bg-card p-8">
          <h2 className="text-lg font-semibold text-foreground text-center mb-1">
            {mode === 'forgot' ? 'Reset Password' : 'Sign In'}
          </h2>
          <p className="text-sm text-muted-foreground text-center mb-6">
            {mode === 'forgot'
              ? 'Enter your email to receive a reset link'
              : 'Sign in to your Bugpunch account'}
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-muted-foreground font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="bg-white/[0.04] border-border text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary/20 h-10"
                placeholder="you@company.com"
              />
            </div>

            {mode === 'login' && (
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs text-muted-foreground font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="bg-white/[0.04] border-border text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary/20 h-10"
                  placeholder="••••••••"
                />
              </div>
            )}

            {error && (
              <div className="text-xs px-3 py-2 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
                {error}
              </div>
            )}

            {info && (
              <div className="text-xs px-3 py-2 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20">
                {info}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-medium mt-1 rounded-lg transition-colors"
            >
              {submitting ? 'Please wait...' : mode === 'forgot' ? 'Send Reset Link' : 'Sign In'}
            </Button>

            <div className="text-center">
              {mode === 'forgot' ? (
                <button
                  type="button"
                  onClick={() => {
                    setMode('login');
                    setError('');
                    setInfo('');
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Back to sign in
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMode('forgot');
                    setError('');
                    setInfo('');
                  }}
                  className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                >
                  Forgot password?
                </button>
              )}
            </div>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          New to Bugpunch?{' '}
          <Link to="/contact" className="text-foreground transition-colors hover:underline">
            Request access
          </Link>
        </p>
      </div>

      {buildCommit && (
        <div className="fixed inset-x-0 bottom-3 select-none text-center text-[10px] text-muted-foreground/40">
          {buildCommit.substring(0, 7)}
        </div>
      )}
    </div>
  );
}
